import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { registerTrFont } from "@/lib/pdfFont";
import { TRY, NUM, fmtDate, unitLabel } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo, logoAspect } from "@/lib/branding";

const NAVY = [11, 15, 25];
const ORANGE = [234, 88, 12];
const GRAY = [107, 114, 128];
const M = 12;
const FOOT = 14;

export const PM_LABEL = { havale: "Havale/EFT", nakit: "Nakit", kredi_karti: "Kredi Kartı", cek: "Çek" };
export const paidOfInv = (inv) => (inv.paid_amount != null ? Number(inv.paid_amount) : inv.paid ? Number(inv.total || 0) : 0);
export const remainOfInv = (inv) => Math.max(0, Number(inv.total || 0) - paidOfInv(inv));
export const payStateOf = (inv) => { const p = paidOfInv(inv); if (p >= Number(inv.total || 0) - 0.005) return "paid"; return p > 0 ? "partial" : "unpaid"; };
export const payStateLabel = (inv) => {
  const s = payStateOf(inv); const sale = inv.type === "satis";
  return s === "paid" ? (sale ? "Tahsil edildi" : "Ödendi") : s === "partial" ? (sale ? "Kısmen tahsil" : "Kısmen ödendi") : (sale ? "Tahsil edilmedi" : "Ödenmedi");
};
export const sourceOf = (inv) => inv.source_type === "efatura_gelen" ? "QNB Gelen e-Fatura" : inv.type === "alis" ? "Manuel Giriş" : inv.qnb?.success ? (inv.qnb.channel_label || "e-Fatura") : "Taslak";
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Süzülen/seçilen faturaları TEK vektörel PDF'te arka arkaya birleştirir (özet sayfa + her fatura ayrı sayfa). */
export async function buildInvoicesPdf(invoices, { contacts = [], subtitle = "" } = {}) {
  const pdf = new jsPDF("p", "mm", "a4");
  const FONT = await registerTrFont(pdf);
  const logo = await loadCompanyLogo();
  const AR = logoAspect();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const W = pageW - M * 2;
  const contactOf = (inv) => contacts.find((c) => c.title === inv.contact_title) || {};
  const saleAll = invoices[0]?.type === "satis";

  const footer = () => {
    pdf.setFillColor(...NAVY); pdf.rect(0, pageH - FOOT, pageW, FOOT, "F");
    pdf.setFont(FONT, "normal"); pdf.setFontSize(7); pdf.setTextColor(215, 220, 230);
    pdf.text(`${COMPANY.owner} · ${COMPANY.name}`, M, pageH - 8.5);
    pdf.setFontSize(6.3); pdf.text(COMPANY.address, M, pageH - 4.5);
    pdf.setFontSize(7); pdf.setTextColor(...ORANGE); pdf.text(COMPANY.email, pageW - M, pageH - 8.5, { align: "right" });
    pdf.setTextColor(215, 220, 230); pdf.setFontSize(6.3);
    pdf.text(`V.D.: ${COMPANY.taxOffice} · TCKN: ${COMPANY.tckn}`, pageW - M, pageH - 4.5, { align: "right" });
  };
  const brand = (y, h) => {
    if (logo) { try { pdf.addImage(logo, "PNG", M, y, h * AR, h); return; } catch (e) { /* noop */ } }
    pdf.setFont(FONT, "bold"); pdf.setFontSize(16); pdf.setTextColor(...NAVY); pdf.text(COMPANY.name, M, y + h * 0.7);
  };
  const companyLines = () => {
    pdf.setFont(FONT, "normal"); pdf.setFontSize(7); pdf.setTextColor(...GRAY);
    pdf.text(COMPANY.address, M, 29.5);
    pdf.text(`${COMPANY.email} · V.D.: ${COMPANY.taxOffice} · TCKN: ${COMPANY.tckn}`, M, 33.5);
  };
  const rule = () => { pdf.setFillColor(...NAVY); pdf.rect(M, 37, W, 0.8, "F"); pdf.setFillColor(...ORANGE); pdf.rect(M, 37, W * 0.28, 0.8, "F"); };

  const invoiceHeader = (inv, cont) => {
    const sale = inv.type === "satis";
    const title = sale ? "SATIŞ FATURASI" : "ALIŞ FATURASI";
    if (cont) {
      brand(6, 9);
      pdf.setFont(FONT, "bold"); pdf.setFontSize(10); pdf.setTextColor(...NAVY);
      pdf.text(`${title} · ${inv.invoice_no || "Taslak"} · ${inv.contact_title || ""} (devam)`, pageW - M, 12, { align: "right" });
      pdf.setFillColor(...ORANGE); pdf.rect(0, 18, pageW, 0.8, "F");
      return 24;
    }
    brand(8, 16); companyLines();
    pdf.setFont(FONT, "bold"); pdf.setFontSize(19); pdf.setTextColor(...NAVY);
    pdf.text(title, pageW - M, 16, { align: "right", charSpace: 0.8 });
    const no = inv.invoice_no ? `No: ${inv.invoice_no}` : "TASLAK";
    pdf.setFontSize(8.5); const bw = pdf.getTextWidth(no) + 10;
    pdf.setFillColor(...ORANGE); pdf.roundedRect(pageW - M - bw, 19.5, bw, 7, 3.5, 3.5, "F");
    pdf.setTextColor(255, 255, 255); pdf.text(no, pageW - M - bw / 2, 24.3, { align: "center" });
    pdf.setFont(FONT, "normal"); pdf.setFontSize(6.5); pdf.setTextColor(...GRAY);
    pdf.text(inv.qnb?.ettn ? `ETTN: ${inv.qnb.ettn}` : sourceOf(inv), pageW - M, 30.5, { align: "right" });
    rule();
    const y0 = 42, bh = 24, lw = W * 0.58 - 2, rx = M + lw + 4, rw = W - lw - 4;
    const c = contactOf(inv);
    pdf.setFillColor(249, 250, 251); pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.25); pdf.roundedRect(M, y0, lw, bh, 2.5, 2.5, "FD");
    pdf.setFillColor(...ORANGE); pdf.rect(M, y0 + 2, 1.1, bh - 4, "F");
    pdf.setFont(FONT, "bold"); pdf.setFontSize(6.5); pdf.setTextColor(...ORANGE);
    pdf.text(sale ? "MÜŞTERİ" : "TEDARİKÇİ", M + 5, y0 + 5.5, { charSpace: 0.4 });
    pdf.setFontSize(12); pdf.setTextColor(...NAVY);
    const nl = pdf.splitTextToSize(String(inv.contact_title || ""), lw - 10).slice(0, 2);
    pdf.text(nl, M + 5, y0 + 11.2);
    const vkn = c.tax_no || inv.qnb?.receiver?.vkn || "";
    const sub = [vkn ? `VKN/TCKN: ${vkn}` : "", c.phone || "", c.address || ""].filter(Boolean).join(" · ");
    if (sub) {
      pdf.setFont(FONT, "normal"); pdf.setFontSize(7); pdf.setTextColor(85, 85, 85);
      pdf.text(pdf.splitTextToSize(sub, lw - 10).slice(0, nl.length === 1 ? 2 : 1), M + 5, y0 + 11.2 + nl.length * 5);
    }
    pdf.setFillColor(...NAVY); pdf.roundedRect(rx, y0, rw, bh, 2.5, 2.5, "F");
    const ml = (label, val, ty, accent, last) => {
      pdf.setFont(FONT, "normal"); pdf.setFontSize(6.4); pdf.setTextColor(148, 163, 184);
      pdf.text(label.toUpperCase(), rx + 4.5, ty, { charSpace: 0.3 });
      pdf.setFont(FONT, "bold"); pdf.setFontSize(8.2);
      if (accent) pdf.setTextColor(...ORANGE); else pdf.setTextColor(255, 255, 255);
      pdf.text(String(val), rx + rw - 4.5, ty, { align: "right", maxWidth: rw - 30 });
      if (!last) { pdf.setDrawColor(40, 48, 66); pdf.setLineWidth(0.2); pdf.line(rx + 4, ty + 2.4, rx + rw - 4, ty + 2.4); }
    };
    ml("Fatura Tarihi", fmtDate(inv.date), y0 + 6.2);
    ml("Vade", inv.due_date ? fmtDate(inv.due_date) : "—", y0 + 12.6);
    ml("Ödeme", `${PM_LABEL[inv.payment_method] || "—"} · ${payStateLabel(inv)}`, y0 + 19, payStateOf(inv) !== "paid", true);
    return y0 + bh + 6;
  };

  const drawInvoice = (inv) => {
    const sale = inv.type === "satis";
    const firstPage = pdf.getNumberOfPages();
    let y = invoiceHeader(inv, false);
    const body = (inv.items || []).map((it, i) => {
      const q = Number(it.quantity || 0), up = Number(it.unit_price || 0), dr = Number(it.discount_rate || 0);
      const nu = up * (1 - dr / 100);
      const subs = [it.code, it.description].filter(Boolean).join(" · ");
      const name = [it.brand, it.name].filter(Boolean).join(" ");
      return [String(i + 1), subs ? `${name}\n${subs}` : name, NUM(q), unitLabel(it.unit, "ADET"), TRY(up), dr ? NUM(dr) : "-", TRY(nu), TRY(q * nu)];
    });
    autoTable(pdf, {
      head: [["#", "Mal / Hizmet", "Miktar", "Birim", "Birim Fiyat", "İsk.%", "Net Fiyat", "Tutar"]],
      body: body.length ? body : [["", "Kalem yok", "", "", "", "", "", ""]],
      startY: y, margin: { left: M, right: M, top: 24, bottom: FOOT + 6 },
      rowPageBreak: "avoid",
      styles: { font: FONT, fontSize: 8, cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 }, textColor: [31, 41, 55], lineColor: [238, 240, 243], lineWidth: { bottom: 0.2 }, overflow: "linebreak", valign: "middle" },
      headStyles: { font: FONT, fontStyle: "bold", fillColor: NAVY, textColor: [255, 255, 255], fontSize: 7.5, halign: "center", minCellHeight: 8 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { cellWidth: 8, halign: "center", textColor: [156, 163, 175] },
        1: { cellWidth: W - 126, fontStyle: "bold" },
        2: { cellWidth: 15, halign: "right" },
        3: { cellWidth: 15, textColor: [85, 85, 85] },
        4: { cellWidth: 24, halign: "right" },
        5: { cellWidth: 12, halign: "right", textColor: ORANGE, fontStyle: "bold" },
        6: { cellWidth: 24, halign: "right" },
        7: { cellWidth: 28, halign: "right", fontStyle: "bold" },
      },
      willDrawCell: (d) => {
        if (d.section === "body" && d.column.index === 1 && Array.isArray(d.cell.text) && d.cell.text.length > 1) { d.cell._lines = d.cell.text; d.cell.text = []; }
      },
      didDrawCell: (d) => {
        const lines = d.cell._lines; if (!lines) return;
        const { x, y: cy } = d.cell; const pad = d.cell.padding("left");
        const lh = 3.4; let ty = cy + (d.cell.height - lines.length * lh) / 2 + lh * 0.78;
        pdf.setFont(FONT, "bold"); pdf.setFontSize(8); pdf.setTextColor(31, 41, 55); pdf.text(lines[0], x + pad, ty); ty += lh;
        pdf.setFont(FONT, "normal"); pdf.setFontSize(6.8); pdf.setTextColor(140, 146, 156);
        for (let k = 1; k < lines.length; k++) { pdf.text(lines[k], x + pad, ty); ty += lh; }
      },
      didDrawPage: (d) => { if (d.pageNumber > firstPage) invoiceHeader(inv, true); footer(); },
    });
    y = pdf.lastAutoTable.finalY + 6;
    const ensure = (need) => { if (y + need > pageH - FOOT - 4) { pdf.addPage(); y = invoiceHeader(inv, true); footer(); } };
    const rightW = 78, rightX = pageW - M - rightW, leftW = rightX - M - 6;
    const totH = (3 + (Number(inv.discount) > 0 ? 1 : 0)) * 7.5 + 16;
    const hist = (inv.payment_history || []).slice(-6);
    const payH = Math.max(20, 15 + hist.length * 4.2 + (inv.note ? 7 : 0));
    ensure(Math.max(totH, payH));
    const top = y;
    // sol: ödeme/tahsilat kartı
    pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.25); pdf.roundedRect(M, top, leftW, payH, 2.5, 2.5, "S");
    const st = payStateOf(inv);
    const col = st === "paid" ? [5, 150, 105] : st === "partial" ? [217, 119, 6] : [220, 38, 38];
    pdf.setFont(FONT, "bold"); pdf.setFontSize(6.5);
    const lbl = payStateLabel(inv).toLocaleUpperCase("tr"); const pw = pdf.getTextWidth(lbl) + 8;
    pdf.setFillColor(...col); pdf.roundedRect(M + 3, top + 3.5, pw, 6, 3, 3, "F");
    pdf.setTextColor(255, 255, 255); pdf.text(lbl, M + 3 + pw / 2, top + 7.4, { align: "center" });
    pdf.setTextColor(...NAVY); pdf.setFontSize(8);
    pdf.text(`${sale ? "Tahsil edilen" : "Ödenen"}: ${TRY(paidOfInv(inv))}   ·   Kalan: ${TRY(remainOfInv(inv))}`, M + pw + 7, top + 7.6);
    let ly = top + 14;
    pdf.setFont(FONT, "normal"); pdf.setFontSize(7); pdf.setTextColor(...GRAY);
    hist.forEach((h) => {
      pdf.text(pdf.splitTextToSize(`${fmtDate(h.date)} · ${PM_LABEL[h.method] || h.method || ""}${h.note ? " · " + h.note : ""}`, leftW - 34)[0], M + 4, ly);
      pdf.text(TRY(h.amount), M + leftW - 4, ly, { align: "right" }); ly += 4.2;
    });
    if (inv.note) { pdf.setTextColor(...NAVY); pdf.text(pdf.splitTextToSize(`Not: ${inv.note}`, leftW - 8)[0], M + 4, ly + 0.5); }
    // sağ: toplamlar
    let ty = top;
    const rline = (label, val) => {
      pdf.setFont(FONT, "normal"); pdf.setFontSize(8.5); pdf.setTextColor(70, 70, 70);
      pdf.text(label, rightX + 1, ty + 5);
      pdf.setFont(FONT, "bold"); pdf.text(String(val), rightX + rightW - 1, ty + 5, { align: "right" });
      pdf.setDrawColor(235, 235, 235); pdf.setLineWidth(0.15); pdf.line(rightX, ty + 7.2, rightX + rightW, ty + 7.2);
      ty += 7.5;
    };
    rline("Liste Toplamı", TRY(inv.subtotal));
    if (Number(inv.discount) > 0) rline(`İskonto${Number(inv.discount_rate) ? ` (%${NUM(inv.discount_rate)} genel)` : ""}`, `- ${TRY(inv.discount)}`);
    rline("Net (Matrah)", TRY(inv.net));
    rline(`KDV (%${NUM(inv.kdv_rate ?? 20)})`, TRY(inv.kdv));
    ty += 2;
    pdf.setFillColor(...ORANGE); pdf.roundedRect(rightX, ty, rightW, 12, 2.5, 2.5, "F");
    pdf.setFont(FONT, "bold"); pdf.setFontSize(9); pdf.setTextColor(255, 255, 255); pdf.text("GENEL TOPLAM", rightX + 4, ty + 7.8);
    pdf.setFontSize(12); pdf.text(TRY(inv.total), rightX + rightW - 4, ty + 8, { align: "right" });
  };

  const drawSummary = () => {
    brand(8, 16); companyLines();
    pdf.setFont(FONT, "bold"); pdf.setFontSize(17); pdf.setTextColor(...NAVY);
    pdf.text(saleAll ? "SATIŞ FATURALARI" : "ALIŞ FATURALARI", pageW - M, 16, { align: "right", charSpace: 0.6 });
    pdf.setFont(FONT, "normal"); pdf.setFontSize(8); pdf.setTextColor(...GRAY);
    if (subtitle) pdf.text(subtitle, pageW - M, 22, { align: "right", maxWidth: W * 0.55 });
    pdf.text(`Oluşturma: ${fmtDate(new Date().toISOString().slice(0, 10))} · ${invoices.length} fatura`, pageW - M, 27.5, { align: "right" });
    rule();
    const sum = (fn) => invoices.reduce((s, i) => s + (Number(fn(i)) || 0), 0);
    const total = sum((i) => i.total), net = sum((i) => i.net), kdv = sum((i) => i.kdv), paid = sum(paidOfInv), remain = sum(remainOfInv);
    const cards = [["TOPLAM", TRY(total), NAVY], ["TOPLAM KDV", TRY(kdv), [71, 85, 105]], [saleAll ? "TAHSİL EDİLEN" : "ÖDENEN", TRY(paid), [5, 150, 105]], ["KALAN", TRY(remain), remain > 0.005 ? [220, 38, 38] : [5, 150, 105]]];
    const cw = (W - 9) / 4;
    cards.forEach(([l, v, c], i) => {
      const x = M + i * (cw + 3);
      pdf.setFillColor(249, 250, 251); pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.25); pdf.roundedRect(x, 42, cw, 16, 2.5, 2.5, "FD");
      pdf.setFillColor(...c); pdf.rect(x, 44, 1.1, 12, "F");
      pdf.setFont(FONT, "bold"); pdf.setFontSize(6.3); pdf.setTextColor(...GRAY); pdf.text(l, x + 5, 47.5, { charSpace: 0.4 });
      pdf.setFontSize(11); pdf.setTextColor(...c); pdf.text(v, x + 5, 54.5);
    });
    const paidLbl = saleAll ? "Tahsil" : "Ödenen";
    autoTable(pdf, {
      head: [["#", "Tarih", "Fatura No", "Cari", "KDV", "Toplam", paidLbl, "Kalan", "Durum"]],
      body: invoices.map((inv, i) => [String(i + 1), fmtDate(inv.date), inv.invoice_no || "Taslak", inv.contact_title || "", TRY(inv.kdv), TRY(inv.total), TRY(paidOfInv(inv)), TRY(remainOfInv(inv)), payStateLabel(inv)]),
      foot: [["", "", "", `TOPLAM (Net ${TRY(net)})`, TRY(kdv), TRY(total), TRY(paid), TRY(remain), ""]],
      startY: 63, margin: { left: M, right: M, top: 20, bottom: FOOT + 6 },
      styles: { font: FONT, fontSize: 7, cellPadding: { top: 1.6, bottom: 1.6, left: 1.2, right: 1.2 }, textColor: [31, 41, 55], lineColor: [238, 240, 243], lineWidth: { bottom: 0.2 }, overflow: "linebreak", valign: "middle" },
      headStyles: { font: FONT, fontStyle: "bold", fillColor: NAVY, textColor: [255, 255, 255], fontSize: 6.8, halign: "center" },
      footStyles: { font: FONT, fontStyle: "bold", fillColor: [243, 244, 246], textColor: NAVY, fontSize: 7 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { cellWidth: 6, halign: "center", textColor: [156, 163, 175] }, 1: { cellWidth: 18 }, 2: { cellWidth: 28 }, 3: { cellWidth: W - 6 - 18 - 28 - 19 - 20 - 19 - 19 - 17, fontStyle: "bold" },
        4: { cellWidth: 19, halign: "right" }, 5: { cellWidth: 20, halign: "right", fontStyle: "bold" }, 6: { cellWidth: 19, halign: "right" }, 7: { cellWidth: 19, halign: "right" }, 8: { cellWidth: 17, fontSize: 6.3 },
      },
      didParseCell: (d) => { if (d.section === "body" && d.column.index === 8) { const s = payStateOf(invoices[d.row.index]); d.cell.styles.textColor = s === "paid" ? [5, 150, 105] : s === "partial" ? [217, 119, 6] : [220, 38, 38]; d.cell.styles.fontStyle = "bold"; } },
      didDrawPage: () => footer(),
    });
  };

  if (invoices.length > 1) drawSummary();
  invoices.forEach((inv, i) => { if (i > 0 || invoices.length > 1) pdf.addPage(); drawInvoice(inv); });
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    pdf.setFont(FONT, "normal"); pdf.setFontSize(6.5); pdf.setTextColor(160, 168, 180);
    pdf.text(`Sayfa ${i} / ${n}`, pageW / 2, pageH - 6, { align: "center" });
  }
  return pdf;
}

/** Faturalar (özet) + Kalemler sayfalı Excel dışa aktarımı. */
export function exportInvoicesExcel(invoices, { contacts = [], filename = "faturalar.xlsx" } = {}) {
  const contactOf = (inv) => contacts.find((c) => c.title === inv.contact_title) || {};
  const sale = invoices[0]?.type === "satis";
  const paidLbl = sale ? "Tahsil Edilen" : "Ödenen";
  const rows = invoices.map((inv) => ({
    "Tarih": fmtDate(inv.date), "Fatura No": inv.invoice_no || "", "Cari": inv.contact_title || "", "VKN/TCKN": contactOf(inv).tax_no || inv.qnb?.receiver?.vkn || "",
    "Vade": inv.due_date ? fmtDate(inv.due_date) : "", "Liste Toplamı": r2(inv.subtotal), "İskonto": r2(inv.discount), "Net": r2(inv.net), "KDV %": Number(inv.kdv_rate ?? 20), "KDV": r2(inv.kdv),
    "Genel Toplam": r2(inv.total), [paidLbl]: r2(paidOfInv(inv)), "Kalan": r2(remainOfInv(inv)), "Durum": payStateLabel(inv), "Ödeme Yöntemi": PM_LABEL[inv.payment_method] || "",
    "Kaynak": sourceOf(inv), "ETTN": inv.qnb?.ettn || "", "Kalem Sayısı": (inv.items || []).length, "Not": inv.note || "",
  }));
  const sum = (k) => r2(rows.reduce((s, r) => s + (Number(r[k]) || 0), 0));
  rows.push({ "Tarih": "", "Fatura No": "", "Cari": "TOPLAM", "Liste Toplamı": sum("Liste Toplamı"), "İskonto": sum("İskonto"), "Net": sum("Net"), "KDV": sum("KDV"), "Genel Toplam": sum("Genel Toplam"), [paidLbl]: sum(paidLbl), "Kalan": sum("Kalan") });
  const lines = invoices.flatMap((inv) => (inv.items || []).map((it, i) => {
    const q = Number(it.quantity || 0), up = Number(it.unit_price || 0), dr = Number(it.discount_rate || 0); const nu = up * (1 - dr / 100);
    return { "Fatura No": inv.invoice_no || "Taslak", "Tarih": fmtDate(inv.date), "Cari": inv.contact_title || "", "#": i + 1, "Ürün / Hizmet": it.name || "", "Marka": it.brand || "", "Kod": it.code || "", "Açıklama": it.description || "",
      "Miktar": q, "Birim": unitLabel(it.unit, "ADET"), "Birim Fiyat": r2(up), "İskonto %": dr, "Net Birim Fiyat": r2(nu), "Tutar": r2(q * nu) };
  }));
  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.json_to_sheet(rows);
  ws1["!cols"] = [12, 20, 34, 14, 12, 14, 12, 14, 7, 12, 14, 14, 12, 16, 14, 18, 38, 8, 30].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws1, "Faturalar");
  const ws2 = XLSX.utils.json_to_sheet(lines.length ? lines : [{ "Bilgi": "Kalem yok" }]);
  ws2["!cols"] = [20, 12, 30, 4, 40, 12, 14, 24, 8, 8, 12, 9, 14, 14].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws2, "Kalemler");
  XLSX.writeFile(wb, filename);
}
